import { useState, useMemo, useEffect } from 'react'
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  MarkerType,
  useNodesState,
  useEdgesState,
} from 'reactflow'
import 'reactflow/dist/style.css'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/utils/cn'
import api from '@/services/api'
import { fluxosNodeTypes } from '@/components/Configuracao/FluxosPipelineNode'
import { buildPipelineFlow, type StageConfigData } from '@/components/Configuracao/fluxosPipelineFlow'
import { flowsData, STATIC_STAGE_CONFIG } from '@/components/Configuracao/fluxosFlowsData'
import { FluxosDealsStepsPanel } from '@/components/Configuracao/FluxosDealsStepsPanel'

export function FluxosTab() {
  const [activeFlowId, setActiveFlowId] = useState<string>(flowsData[0].id)
  const [stageConfig, setStageConfig] = useState<StageConfigData | null>(null)
  const [configLoading, setConfigLoading] = useState(true)

  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])

  useEffect(() => {
    api
      .get('/deals/stage-config')
      .then((response) => {
        setStageConfig(response.data.data)
        setConfigLoading(false)
      })
      .catch((err) => {
        console.error('Erro ao carregar stage-config, usando fallback estático:', err)
        setStageConfig(STATIC_STAGE_CONFIG)
        setConfigLoading(false)
      })
  }, [])

  const dynamicDealsFlow = useMemo(() => {
    if (!stageConfig) return null
    return buildPipelineFlow(stageConfig)
  }, [stageConfig])

  const activeFlow = useMemo(() => {
    if (activeFlowId === 'deals' && dynamicDealsFlow) {
      return {
        id: 'deals',
        title: 'Deals & Pipeline',
        description: 'Fluxo operacional de fechamento e transição de estágios (Kanban)',
        nodes: dynamicDealsFlow.nodes,
        edges: dynamicDealsFlow.edges,
        steps: [] as { id: number; title: string; desc: string }[],
      }
    }
    return flowsData.find((f) => f.id === activeFlowId)!
  }, [activeFlowId, dynamicDealsFlow])

  useEffect(() => {
    setNodes(activeFlow.nodes)
    setEdges(
      activeFlow.edges.map((edge) => ({
        ...edge,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: edge.style?.stroke || 'var(--brand-primary)',
        },
      })),
    )
  }, [activeFlow, setNodes, setEdges])

  const isDynamicDeals = activeFlowId === 'deals' && !!stageConfig

  return (
    <div className="flex flex-col lg:flex-row gap-4 h-[calc(100vh-280px)] min-h-[600px]">
      <div className="flex-1 glass-card p-0 overflow-hidden relative border-white/10 rounded-xl">
        {activeFlowId === 'deals' && configLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-pulse space-y-3 w-3/4">
              <div className="h-5 bg-white/10 rounded w-48" />
              <div className="h-3 bg-white/10 rounded w-72" />
              <div className="grid grid-cols-4 gap-4 mt-12">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-24 bg-white/5 rounded-xl" />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="absolute top-4 left-4 z-10 pointer-events-none">
              <h2 className="text-xl font-bold text-white tracking-wide">{activeFlow.title}</h2>
              <p className="text-sm text-text-muted mt-1">{activeFlow.description}</p>
            </div>

            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={fluxosNodeTypes}
              fitView
              fitViewOptions={{ padding: 0.2 }}
              className="bg-bg-main"
              proOptions={{ hideAttribution: true }}
            >
              <Background color="#fff" gap={16} style={{ opacity: 0.05 }} />
              <Controls className="bg-[#141516] border border-white/10 fill-white" />
              <MiniMap
                nodeStrokeColor="#666"
                nodeColor="#1a1a2e"
                nodeBorderRadius={4}
                className="bg-[#0a0a0a] border border-white/10 rounded-lg"
              />
            </ReactFlow>
          </>
        )}
      </div>

      <div className="w-full lg:w-80 flex flex-col gap-4">
        <div className="glass-card flex-1 flex flex-col overflow-hidden">
          <h3 className="text-xs font-bold text-text-muted uppercase tracking-wider mb-4 shrink-0">
            Fluxos Mapeados
          </h3>
          <div className="flex-1 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
            {flowsData.map((flow) => (
              <button
                key={flow.id}
                type="button"
                onClick={() => setActiveFlowId(flow.id)}
                className={cn(
                  'w-full text-left p-3 rounded-xl border transition-all duration-200',
                  activeFlowId === flow.id
                    ? 'border-accent bg-accent/10'
                    : 'border-white/5 bg-white/[0.02] hover:bg-white/[0.06] hover:border-white/10',
                )}
              >
                <div
                  className={cn(
                    'font-bold text-sm mb-1',
                    activeFlowId === flow.id ? 'text-accent' : 'text-text-primary',
                  )}
                >
                  {flow.title}
                </div>
                <div className="text-xs text-text-muted leading-relaxed">{flow.description}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="glass-card flex-1 flex flex-col overflow-hidden">
          <h3 className="text-xs font-bold text-text-muted uppercase tracking-wider mb-4 shrink-0">
            Passo a Passo
          </h3>
          <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-4">
            {isDynamicDeals && stageConfig ? (
              <FluxosDealsStepsPanel stageConfig={stageConfig} />
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeFlow.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                  {activeFlow.steps.map((step) => (
                    <div key={step.id} className="flex gap-3 group">
                      <div className="shrink-0 flex flex-col items-center">
                        <div className="w-6 h-6 rounded-full bg-accent/20 border border-accent/30 text-accent flex items-center justify-center text-xs font-bold group-hover:bg-accent group-hover:text-white transition-colors">
                          {step.id}
                        </div>
                        <div className="w-px h-full bg-white/5 my-1 group-last:hidden" />
                      </div>
                      <div className="pb-4">
                        <h4 className="text-sm font-bold text-text-primary mb-1">{step.title}</h4>
                        <p className="text-xs text-text-muted leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
